import React, { useEffect, useState } from 'react';
import { Head, usePage } from '@inertiajs/react';
import GoodsList from './components/GoodsList';
import Timeline from './components/Timeline';
import SubscribeForm from './components/SubscribeForm';
import DeliveryPreferencesForm from './components/DeliveryPreferencesForm';
import Glyph from '../../components/Glyph';
import { useTrans } from '../../hooks/useTrans';

const TEMPERATURE_POLL_INTERVAL = 60 * 1000;

function TransportAddress({ glyph, label, children }) {
    return (
        <div className="transport-address">
            <Glyph name={glyph} />
            <div className="transport-address__inner">
                <h6 className="transport__label">{label}</h6>
                {children}
            </div>
        </div>
    );
}

function TransportRow({ label, value }) {
    return (
        <div className="track-trace-transport-card__row">
            <span className="track-trace-transport-card__row-label">{label}</span>
            <span className="track-trace-transport-card__row-value">{value ?? '–'}</span>
        </div>
    );
}

function useLiveTemperatures(traceUuid, goods, isAuthenticated) {
    const [liveTemperatures, setLiveTemperatures] = useState({});

    useEffect(() => {
        if (!isAuthenticated) {
            return undefined;
        }

        const fetchTemperatures = async () => {
            try {
                const response = await fetch(`/ajax/track-and-trace/${traceUuid}`, {
                    headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' },
                });

                if (!response.ok) {
                    return;
                }

                const { goods: updatedGoods } = await response.json();

                setLiveTemperatures(
                    Object.fromEntries(
                        Object.values(updatedGoods ?? {}).map((good) => [good.id, good.currentTemperature])
                    )
                );
            } catch {
                return;
            }
        };

        const interval = setInterval(fetchTemperatures, TEMPERATURE_POLL_INTERVAL);

        return () => clearInterval(interval);
    }, [traceUuid, isAuthenticated]);

    return goods.map((good) => (
        good.id in liveTemperatures
            ? { ...good, currentTemperature: liveTemperatures[good.id] }
            : good
    ));
}

export default function TrackAndTraceShow({
    traceUuid,
    email,
    activeGoodId,
    orderIsCompleted,
    order,
    deliveryAddress,
    transport,
    timeline,
    timelineNotice,
    goods,
    temperature,
    deliveryPreferences,
    showSubscribeForm,
    fromOrderId,
    backUrl,
}) {
    const translate = useTrans();
    const { auth } = usePage().props;
    const isAuthenticated = Boolean(auth);

    const goodsWithLiveTemperatures = useLiveTemperatures(traceUuid, goods, isAuthenticated);

    const hasTransportMeta = transport.showDriverName || transport.showVehicleRegistration;
    const showTransportBlock = hasTransportMeta || deliveryPreferences.show;

    const subscribeForm = <SubscribeForm traceUuid={traceUuid} fromOrderId={fromOrderId} email={email} />;

    return (
        <div className="panel panel--alt">
            <Head title={`Track & Trace – ${translate('Order')} #${order.externalOrderId}`} />

            <div className="panel__main panel__main--larger-gutter-mobile panel__main--larger-gutter">
                {backUrl && (
                    <p>
                        <a href={backUrl}>&laquo; {translate('Terug naar orderdetails')}</a>
                    </p>
                )}

                <div className="track-trace-page__intro flex flex--column-mobile flex--space-between">
                    <div className="flex-item flex-item--column">
                        <h3>{translate('Order')} #{order.externalOrderId}</h3>
                        <p>
                            {order.reference && <>{order.labelReference}: {order.reference}<br /></>}
                            {order.orderReference && <>{order.labelCustomerReference}: {order.orderReference}</>}
                        </p>
                    </div>

                    <div className="flex-item flex-item--addresses">
                        <TransportAddress glyph="glyph-house" label={translate('Opdrachtgever')}>
                            {order.debtor}
                        </TransportAddress>

                        <TransportAddress glyph="glyph-destination" label={translate('Naar')}>
                            {deliveryAddress.name}<br />
                            {deliveryAddress.street}<br />
                            {deliveryAddress.zipCity}<br />
                            {deliveryAddress.country}
                        </TransportAddress>
                    </div>
                </div>

                {showTransportBlock && (
                    <>
                        <hr className="hr hr--large" />

                        <div className="track-trace-transport-card__header">
                            <div className="track-trace-transport-card__icon" aria-hidden="true">
                                <Glyph name="glyph-package" />
                            </div>
                            <h4 className="track-trace-transport-card__title">{translate('Transportinformatie')}</h4>
                        </div>

                        <div className="track-trace-transport-card__body">
                            {hasTransportMeta && (
                                <div className="track-trace-transport-card__col track-trace-transport-card__col--meta">
                                    <div className="track-trace-transport-card__meta">
                                        {transport.showDriverName && (
                                            <TransportRow label={translate('Uw chauffeur')} value={transport.driverName} />
                                        )}
                                        {transport.showVehicleRegistration && (
                                            <TransportRow label={translate('Kenteken')} value={transport.vehicleLicencePlate} />
                                        )}
                                    </div>
                                </div>
                            )}

                            {deliveryPreferences.show && (
                                <div className="track-trace-transport-card__col track-trace-transport-card__col--form">
                                    <DeliveryPreferencesForm
                                        traceUuid={traceUuid}
                                        fromOrderId={fromOrderId}
                                        deliveryPreferences={deliveryPreferences}
                                        orderIsCompleted={orderIsCompleted}
                                    />
                                </div>
                            )}
                        </div>
                    </>
                )}

                <hr className="hr hr--large hr--hide-mobile" />

                <div className="row track-trace-page__main-row">
                    <div className="scol12 pcol12 tcol12 col7">
                        {showSubscribeForm && <div className="hide show--widescreen-and-larger">{subscribeForm}</div>}

                        <h3 className="order-heading order-heading--goods-table">{translate('Te vervoeren goederen')}</h3>

                        <GoodsList
                            goods={goodsWithLiveTemperatures}
                            activeGoodId={activeGoodId}
                            traceUuid={traceUuid}
                            fromOrderId={fromOrderId}
                            temperature={temperature}
                            isAuthenticated={isAuthenticated}
                        />
                    </div>

                    <div className="scol12 pcol12 tcol12 col5">
                        <div className="track-trace--timeline">
                            <Timeline events={timeline} notice={timelineNotice} />
                        </div>

                        {showSubscribeForm && <div className="hide--widescreen-and-larger">{subscribeForm}</div>}
                    </div>
                </div>
            </div>
        </div>
    );
}
